import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRightIcon, InfoIcon, ShieldIcon, TruckIcon, CheckCircleIcon, EyeIcon, BadgeCheckIcon } from 'lucide-react';
import { VendorCard } from '../components/VendorCard';
import { RecommendedDealer } from '../components/RecommendedDealer';
import { StickyAddToCart } from '../components/StickyAddToCart';
import { YouMayAlsoLike } from '../components/YouMayAlsoLike';
import { CustomerReviews } from '../components/CustomerReviews';
import { ExpandableSpecs } from '../components/ExpandableSpecs';
import { CertifiedQuality } from '../components/CertifiedQuality';
import { SocialProof } from '../components/SocialProof';
export const ProductDetailPage = () => {
  const {
    productId
  } = useParams();
  const [selectedStorage, setSelectedStorage] = useState('128GB');
  const [selectedColor, setSelectedColor] = useState('Black');
  const [selectedCondition, setSelectedCondition] = useState('Fair');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  // Mock product data
  const product = {
    id: 'iphone-15',
    name: 'Samsung - Galaxy S24 - 256GB - Onyx Black - Dual Sim',
    price: 658,
    originalPrice: 1299,
    discountPercentage: 49,
    images: ['https://uploadthingy.s3.us-west-1.amazonaws.com/adSmqXZtwMRaADCVDQGH75/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/adSmqXZtwMRaADCVDQGH75/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/adSmqXZtwMRaADCVDQGH75/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/adSmqXZtwMRaADCVDQGH75/image.png'],
    warranty: '3 months warranty',
    returnPolicy: 'Free 14 days return',
    deliveryDate: 'Get it by 9 May',
    storageOptions: ['128GB', '256GB', '512GB', '1TB'],
    colorOptions: ['Black', 'Blue', 'Green', 'Yellow'],
    conditionOptions: ['Fair', 'Good', 'Excellent', 'Brand New'],
    soldCount: 780,
    specifications: {
      modelName: 'Samsung Galaxy S24',
      dimensions: {
        metric: '147 x 70.6 x 7.6 mm',
        imperial: '(5.79 x 2.78 x 0.30 in)'
      },
      weight: {
        metric: '167 g',
        imperial: '(5.89 oz)'
      },
      build: 'Glass front (Gorilla Glass Victus 2), glass back (Gorilla Glass Victus 2), aluminium frame',
      sim: 'Nano-SIM and eSIM or Dual SIM (2 Nano-SIMs and eSIM, dual stand-by) | Armor aluminium 2 frame with tougher drop and scratch resistance (advertised)',
      displayType: 'Dynamic LTPO AMOLED 2X, 120Hz, HDR10+, 2600 nits (peak)',
      displaySize: '6.2 inches, 94.4 cm2 (~90.9% screen-to-body ratio)',
      additionalSpecs: {
        Resolution: '1080 x 2340 pixels, 19.5:9 ratio (~416 ppi density)',
        OS: 'Android 14, One UI 6.1',
        Chipset: 'Exynos 2400 (4 nm) - International',
        CPU: 'Deca-core (1x3.2 GHz Cortex-X4 & 2x2.9 GHz Cortex-X4 & 3x2.6 GHz Cortex-A720 & 4x1.95 GHz Cortex-A520)',
        GPU: 'Xclipse 940',
        Memory: 'microSDXC (dedicated slot)',
        Battery: 'Li-Ion 4000 mAh, non-removable'
      }
    }
  };
  // Mock vendor data
  const vendors = [{
    id: 'mister-mobile',
    name: 'Mister Mobile',
    rating: 4.7,
    price: 658,
    originalPrice: 1299,
    condition: 'Excellent',
    deliveryTime: '2-3 days',
    inStock: true
  }, {
    id: 'wmart',
    name: 'Wmart',
    rating: 4.7,
    price: 465,
    originalPrice: 699,
    condition: 'Fair',
    deliveryTime: '1-2 days',
    inStock: true
  }, {
    id: 'gamextreme',
    name: 'GameXtreme',
    rating: 4.7,
    price: 465,
    originalPrice: 699,
    condition: 'Fair',
    deliveryTime: '3-5 days',
    inStock: true
  }, {
    id: 'mobile-relation',
    name: 'Mobile Relation',
    rating: 4.7,
    price: 465,
    originalPrice: 699,
    condition: 'Fair',
    deliveryTime: '2-4 days',
    inStock: true
  }];
  // Add trust badges section
  const trustBadges = [{
    icon: <ShieldIcon className="h-6 w-6" data-id="element-2916" />,
    title: '30-Day Money Back',
    description: 'No questions asked'
  }, {
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-2917" />,
    title: 'Verified Quality',
    description: 'All devices tested'
  }, {
    icon: <TruckIcon className="h-6 w-6" data-id="element-2918" />,
    title: 'Free Shipping',
    description: 'On orders over $50'
  }];
  return <main className="container mx-auto px-4 py-6" data-id="element-2919">
      {/* Sticky Add to Cart */}
      <StickyAddToCart product={{
      name: product.name,
      condition: selectedCondition,
      price: product.price,
      originalPrice: product.originalPrice,
      discountPercentage: product.discountPercentage
    }} data-id="element-2920" />
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-gray-500 mb-6" data-id="element-2921">
        <Link to="/" className="hover:text-blue-600" data-id="element-2922">
          Home
        </Link>
        <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2923" />
        <Link to="/category/smart-phones" className="hover:text-blue-600" data-id="element-2924">
          Buy Phones
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-2925">
        <div className="flex flex-col md:flex-row gap-8" data-id="element-2926">
          {/* Product images */}
          <div id="product-image-section" className="w-full md:w-2/5" data-id="element-2927">
            <div className="flex items-center justify-between mb-2" data-id="element-2928">
              <div className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full flex items-center" data-id="element-2929">
                <span className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-2930"></span>
                Sustainable choice
              </div>
              <div className="flex items-center text-gray-700 text-sm" data-id="element-2931">
                <span className="font-medium mr-1" data-id="element-2932">
                  {product.soldCount} Sold
                </span>
                <div className="h-4 w-4 text-red-500 mr-1" data-id="element-2933" />
                <span data-id="element-2934">Selling Fast!</span>
              </div>
            </div>
            <div className="mb-4 border border-gray-200 rounded-lg p-4 flex justify-center" data-id="element-2935">
              <img src={product.images[activeImageIndex]} alt={product.name} className="w-full max-w-xs object-contain" data-id="element-2936" />
            </div>
            <div className="flex space-x-2 mb-4" data-id="element-2937">
              {product.images.map((image, index) => <button key={index} className={`border ${activeImageIndex === index ? 'border-blue-500' : 'border-gray-200'} rounded-lg p-2 w-20 h-20`} onClick={() => setActiveImageIndex(index)} data-id="element-2938">
                  <img src={image} alt={`${product.name} thumbnail ${index + 1}`} className="w-full h-full object-contain" data-id="element-2939" />
                </button>)}
            </div>
            <div className="mb-6 flex flex-wrap gap-4" data-id="element-2940">
              <div className="flex items-center" data-id="element-2941">
                <span className="inline-block h-4 w-4 rounded-full bg-blue-600 mr-2" data-id="element-2942"></span>
                <span className="text-sm" data-id="element-2943">6.2 inches</span>
              </div>
              <div className="flex items-center" data-id="element-2944">
                <span className="inline-block h-4 w-4 rounded-full bg-blue-600 mr-2" data-id="element-2945"></span>
                <span className="text-sm" data-id="element-2946">50 MP</span>
              </div>
              <div className="flex items-center" data-id="element-2947">
                <span className="inline-block h-4 w-4 rounded-full bg-blue-600 mr-2" data-id="element-2948"></span>
                <span className="text-sm" data-id="element-2949">Li-Ion 4000 mAh</span>
              </div>
            </div>
            {/* Social Proof */}
            <SocialProof data-id="element-2950" />
            {/* Trust badges */}
            <div className="mt-8 border-t border-gray-200 pt-6" data-id="element-2951">
              <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2952">
                Why buy from SellUp?
              </h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-2953">
                {trustBadges.map((badge, index) => <div key={index} className="flex flex-col items-center text-center" data-id="element-2954">
                    <div className="text-blue-600 mb-2" data-id="element-2955">{badge.icon}</div>
                    <h4 className="font-medium text-gray-800" data-id="element-2956">{badge.title}</h4>
                    <p className="text-xs text-gray-500" data-id="element-2957">{badge.description}</p>
                  </div>)}
              </div>
            </div>
            <div className="mt-6" data-id="element-2958">
              <button className="w-full border border-red-500 text-red-500 rounded-full py-2 flex items-center justify-center" data-id="element-2959">
                <span className="h-2 w-2 bg-red-500 rounded-full mr-2" data-id="element-2960"></span>
                SPECIFICATIONS
              </button>
            </div>
          </div>
          {/* Product details */}
          <div className="w-full md:w-3/5" data-id="element-2961">
            <div className="flex justify-between items-start mb-4" data-id="element-2962">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-2963">
                {product.name}
              </h1>
              <div className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-2964">
                {product.discountPercentage}% OFF
              </div>
            </div>
            <div className="mb-6 flex items-center space-x-6" data-id="element-2965">
              <div className="flex items-center text-gray-600" data-id="element-2966">
                <ShieldIcon className="h-5 w-5 mr-2" data-id="element-2967" />
                <span data-id="element-2968">{product.warranty}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2969">
                <TruckIcon className="h-5 w-5 mr-2" data-id="element-2970" />
                <span data-id="element-2971">{product.returnPolicy}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2972">
                <InfoIcon className="h-5 w-5 mr-2" data-id="element-2973" />
                <span data-id="element-2974">{product.deliveryDate}</span>
              </div>
            </div>
            <div className="mb-6" data-id="element-2975">
              <div className="text-3xl font-bold text-gray-900" data-id="element-2976">
                SGD {product.price.toFixed(2)}
              </div>
              <div className="flex items-center" data-id="element-2977">
                <span className="text-gray-500 line-through mr-2" data-id="element-2978">
                  SGD {product.originalPrice.toFixed(2)}
                </span>
                <span className="text-sm text-red-600" data-id="element-2979">new</span>
              </div>
            </div>
            {/* Storage options */}
            <div className="mb-6" data-id="element-2980">
              <h3 className="text-gray-500 uppercase text-sm font-medium mb-2" data-id="element-2981">
                STORAGE
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-2982">
                {product.storageOptions.map(storage => <button key={storage} className={`px-6 py-2 rounded-md border ${selectedStorage === storage ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-700'}`} onClick={() => setSelectedStorage(storage)} data-id="element-2983">
                    {storage}
                  </button>)}
              </div>
            </div>
            {/* Color options */}
            <div className="mb-6" data-id="element-2984">
              <h3 className="text-gray-500 uppercase text-sm font-medium mb-2" data-id="element-2985">
                COLOR
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-2986">
                {product.colorOptions.map(color => <button key={color} className={`px-6 py-2 rounded-md border ${selectedColor === color ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-700'}`} onClick={() => setSelectedColor(color)} data-id="element-2987">
                    {color}
                  </button>)}
              </div>
            </div>
            {/* Condition options */}
            <div className="mb-6" data-id="element-2988">
              <h3 className="text-gray-500 uppercase text-sm font-medium mb-2" data-id="element-2989">
                CONDITION
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-2990">
                {product.conditionOptions.map(condition => <button key={condition} className={`px-6 py-2 rounded-md border ${selectedCondition === condition ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-700'}`} onClick={() => setSelectedCondition(condition)} data-id="element-2991">
                    {condition}
                  </button>)}
              </div>
            </div>
            <div className="mb-8" data-id="element-2992">
              <button className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-colors font-medium text-lg" data-id="element-2993">
                Add to Cart
              </button>
            </div>
            {/* Expandable Specifications */}
            <div className="space-y-4 mb-8" data-id="element-2994">
              <ExpandableSpecs specifications={product.specifications} data-id="element-2995" />
              <CertifiedQuality data-id="element-2996" />
            </div>
            {/* Vendor comparison section */}
            <div className="mt-8" data-id="element-2997">
              <h3 className="text-lg font-bold text-gray-800 mb-4" data-id="element-2998">
                CERTIFIED & SOLD BY
              </h3>
              {/* Recommended dealer */}
              <RecommendedDealer vendor={vendors[0]} data-id="element-2999" />
              {/* Other vendors */}
              <div className="space-y-4" data-id="element-3000">
                {vendors.slice(1).map(vendor => <VendorCard key={vendor.id} vendor={vendor} data-id="element-3001" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Additional sections */}
      <CustomerReviews data-id="element-3002" />
      <YouMayAlsoLike data-id="element-3003" />
    </main>;
};